import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { closeModal } from '../../actions/modal';
import { Input, SelectField } from '../../containers/Form';
import { actions as kitActions } from '../../pages/Kits/redux/actions';
import { ASSIGN_DEVICE_TO_KIT_ERROR, KITS_LIST_RESULT } from '../../pages/Kits/redux/constants';
import { actions } from '../../pages/Organizations/redux/actions';
import Strings from '../../Strings';
import DeviceList from './DeviceList';
import Wizard from '../../containers/Modal/Wizard';
import { notificationActions } from '../../components/Notification/redux/actions';

export const MAX_KIT_NAME_LENGTH = 50;
const MAX_DEVICES_IN_SELECT = 200;

function CreateKitAndAddDevices(props) {
  const { assignDeviceToKit, organizations, getKits, getOrgs, data, showNotification, parentRefresh, onCancel, onSubmit, getDevicesUnassignedToKit } = props;
  const kitId = data?.kitId;
  const [kitName, setKitName] = React.useState(data?.kitName);
  const [selectedOrgId, setselectedOrgId] = React.useState(data?.orgId);
  const [devices, setDevices] = React.useState([]);
  const [unassignedDevices, setUnassignedDevices] = React.useState([]);
  const [asyncSelectNeeded, setAsyncSelectNeeded] = React.useState(false);
  const [highlightInvalidFields, setHighlightMissingFields] = React.useState(false);

  const editMode = data?.kitId;

  React.useEffect(() => {
    if (!organizations) {
      getOrgs();
    }

    if (editMode) {
      getKits({ search: kitId }).then(resp => {
        if (resp.type === KITS_LIST_RESULT) {
          setselectedOrgId(resp.response.kits[0].org_id);
        }
      });
    }
  }, []);

  React.useEffect(() => {
    if (selectedOrgId) {
      loadCaps(selectedOrgId);
    }
  }, [selectedOrgId]);

  const attachDevices = (kit_id, deviceList = []) => {
    const results = [];

    deviceList.forEach(device => {
      assignDeviceToKit(kit_id, { device_id: device.device_id }).then(r => {
        if (r.type === ASSIGN_DEVICE_TO_KIT_ERROR) {
          showNotification(r.response?.data?.error?.message, 5000, true);
        } else {
          results.push(r);

          if (results.length === devices.length) {
            parentRefresh();
            onCancel();
          }
        }
      });
    });
  };

  const onSave = async () => {
    const realDevices = devices.filter(e => e.device_id !== '');
    const areThereAny = realDevices.length > 0;

    const request = { name: kitName };
    if (editMode) {
      request.id = kitId;
    } else {
      request.org_id = selectedOrgId;
    }

    let id = editMode ? kitId : null;
    if (!editMode || kitName !== data?.kitName) {
      const resp = await onSubmit(request);

      if (resp.type === `${data.actionType}/error`) {
        showNotification(resp.response?.data?.error?.message, 5000, true);
      } else {
        id = resp.response.kit.id;
      }
    }

    if (areThereAny) {
      attachDevices(id, devices);
    } else {
      parentRefresh();
      onCancel();
    }
  };

  const deleteDevice = record => {
    setDevices(d => d.filter(r => r !== record));
  };

  const loadCaps = orgId => {
    setDevices([]);
    getDevicesUnassignedToKit({ orgId, limit: MAX_DEVICES_IN_SELECT }).then(resp => {
      const respData = resp.response.devices.map(e => {
        return {
          value: e.device_id,
          device_id: e.device_id,
          label: `${e.device_id}${e.device_name ? ` (${e.device_name})` : ''}`,
          device_name: e.device_name,
        };
      });
      setUnassignedDevices(respData);
      setAsyncSelectNeeded(resp.response.pagination.totalRecords > MAX_DEVICES_IN_SELECT);
    });
  };

  React.useEffect(() => {
    if (!devices?.length) {
      return;
    }
    setUnassignedDevices(d => d.filter(u => !devices.some(v => v.device_id === u.device_id)));
  }, [devices]);

  const loadOptions = (value, callback) => {
    if (!value || value.length < 3) {
      return;
    }
    setTimeout(() => {
      getDevicesUnassignedToKit({ search: value, orgId: selectedOrgId }).then(resp =>
        callback(() => {
          const respData = resp.response.devices.map(e => {
            return {
              value: e.device_id,
              device_id: e.device_id,
              label: `${e.device_id}${e.device_name ? ` (${e.device_name})` : ''}`,
              device_name: e.device_name,
            };
          });
          return respData.filter(d => !devices.some(dv => dv.device_id === d.device_id));
        }),
      );
    }, 1000);
  };

  const listOfDevices = () => {
    const realDevices = devices.filter(e => e.device_id !== '');
    const areThereDevices = realDevices.length > 0;
    return (
      <>
        {areThereDevices && <label className="header-label">{Strings.devices}:</label>}
        {areThereDevices &&
          realDevices.map(device => (
            <li className="mb-20" key={device.device_id}>
              {device.label}
            </li>
          ))}
      </>
    );
  };

  const dataPage = (
    <>
      {!editMode && (
        <SelectField
          name="organization"
          label={Strings.organization}
          key={`org-list-${organizations?.length}`}
          onChange={option => setselectedOrgId(option.masked_id)}
          data={organizations?.map(org => ({
            value: org.masked_id,
            label: org.name,
            masked_id: org.masked_id,
          })) || []}
          isSearchable
          isRequired
          searchByLabelOnly
          highlightInvalid={highlightInvalidFields}
          value={selectedOrgId}
          placeholder={Strings.select}
        />
      )}
      <Input
        name="kit-name"
        id="kit-name"
        label={Strings.kitName}
        value={kitName}
        onChange={e => setKitName(e.target.value)}
        maxLength={MAX_KIT_NAME_LENGTH}
        highlightInvalid={highlightInvalidFields}
      />
      <DeviceList
        asyncNeeded={asyncSelectNeeded}
        delete={deleteDevice}
        devices={devices}
        data={unassignedDevices}
        handleChange={option => setDevices(d => d.concat(option))}
        loadOptions={loadOptions}
      />
    </>
  );
  const reviewPage = (
    <>
      <label className="header-label">
        {Strings.kitName}: {kitName || 'N/A'}
      </label>
      {editMode ? null : (
        <label className="header-label">
          {Strings.organization}: {organizations?.find(o => o.masked_id === selectedOrgId)?.name}
        </label>
      )}
      {listOfDevices()}
    </>
  );

  const pages = [
    {
      id: 'edit-kit',
      title: editMode ? Strings.editKit : Strings.createKit,
      content: dataPage,
      emptyFieldsCount: selectedOrgId ? 0 : 1,
      canGoNext: !!selectedOrgId,
      nextButton: { text: Strings.next },
    },
    {
      id: 'review',
      title: Strings.review,
      content: reviewPage,
      canGoNext: true,
      nextButton: { text: editMode ? Strings.save : Strings.create },
    },
  ];

  return (
    <Wizard
      name="create-kit-and-add-devices"
      pages={pages}
      onNextButtonHover={e => setHighlightMissingFields(e)}
      onSubmit={onSave}
      onCancelAction={onCancel}
      showPagesFilter={false}
    />
  );
}

CreateKitAndAddDevices.propTypes = {
  assignDeviceToKit: PropTypes.func,
  getDevicesUnassignedToKit: PropTypes.func,
  getKits: PropTypes.func,
  getOrgs: PropTypes.func,
  organizations: PropTypes.array,
  data: PropTypes.object,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  parentRefresh: PropTypes.func,
  showNotification: PropTypes.func,
};

const mapStateToProps = state => ({
  AttachCaptoAdminModalLoading: state.entities.caps.loading,
  organizations: state.superUser?.organizations?.organizationAll,
  devices: state.entities.kits?.devices,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  onCancel: () => dispatch(closeModal('create-kit-and-add-devices')),
  getOrgs: request => dispatch(actions.getOrgs(request)),
  getKits: pageRequest => dispatch(kitActions.getKits(pageRequest)),
  onSubmit: request => dispatch(ownProps.data.action(request)),
  assignDeviceToKit: (kitId, data) => dispatch(kitActions.assignDeviceToKit(kitId, data)),
  getDevicesUnassignedToKit: pageRequest => dispatch(kitActions.getDevicesUnassignedToKit(pageRequest)),
  parentRefresh: () => ownProps.data.onRefresh(),
  showNotification: (message, timeout, isError) => dispatch(notificationActions.show(message, timeout, isError)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateKitAndAddDevices);
